import type { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';

import {
  HELPFUL_HEADING,
  MUST_INCLUDE_HEADING,
  ROSTER_HELPFUL_KEYS,
  ROSTER_MUST_INCLUDE_KEYS,
  rosterFieldLabels,
} from './rosterFieldLabels';
import { INFO_BOX, LTEL_BANNER } from '../IndividualPlan/tokens';

const INK = '#08081B';
const TEXT_SECONDARY = '#63637D';
const TEXT_INVERTED = '#FFFFFF';
const GREY_25 = '#F6F6F8';
const GREY_50 = '#E3E3E8';
const GREY_300 = '#7F7F99';
const GREY_600 = '#3C3C60';
const DIVIDER = 'rgba(8, 8, 27, 0.15)';
const INDIGO_600 = '#2F2F9D';
const BLUE_100 = '#CCE8FA';
const BLUE_800 = '#0B3D7F';
const BLUE_900 = '#072254';

const WIDA_HEADERS = [
  'State Student ID',
  'Test Completion Date',
  'Scale Score - Overall',
  'Proficiency Level - Overall',
  'Do Not Score Code - Listening',
];

const SIS_MUST_INCLUDE = rosterFieldLabels(ROSTER_MUST_INCLUDE_KEYS);

const SIS_HELPFUL = rosterFieldLabels(ROSTER_HELPFUL_KEYS);

function NumberBadge({ n, size = 22 }: { n: ReactNode; size?: number }) {
  return (
    <Box
      data-testid="help-number-badge"
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: INDIGO_600,
        color: TEXT_INVERTED,
        fontSize: size >= 26 ? 16 : 12,
        lineHeight: size >= 26 ? '20px' : '16px',
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
  );
}

function FileRow({ n, title, subtitle }: { n: number; title: string; subtitle: string }) {
  return (
    <Box
      data-testid="help-file-row"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        bgcolor: GREY_25,
        borderRadius: '12px',
        p: 2,
      }}
    >
      <NumberBadge n={n} size={26} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: '20px', color: INK }}>
          {title}
        </Typography>
        <Typography
          sx={{ fontWeight: 400, fontSize: 12, lineHeight: '16px', color: TEXT_SECONDARY }}
        >
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: BLUE_100,
          color: BLUE_800,
          borderRadius: '8px',
          px: 1,
          py: 0.5,
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 700,
          letterSpacing: '0.5px',
          flexShrink: 0,
        }}
      >
        REQUIRED
      </Box>
    </Box>
  );
}

function ChipRow({ chips, variant }: { chips: string[]; variant: 'blue' | 'grey' }) {
  const styles =
    variant === 'blue' ? { bgcolor: BLUE_100, color: BLUE_900 } : { bgcolor: GREY_50, color: INK };
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {chips.map((chip) => (
        <Box
          key={chip}
          sx={{
            ...styles,
            borderRadius: '9999px',
            px: 1,
            py: 0.5,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '16px',
          }}
        >
          {chip}
        </Box>
      ))}
    </Box>
  );
}

function SectionHeading({ n, title }: { n: number; title: string }) {
  return (
    <Box
      data-testid={`help-section-heading-${n}`}
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
    >
      <NumberBadge n={n} />
      <Typography
        data-testid="help-section-title"
        sx={{ fontWeight: 800, fontSize: 16, lineHeight: '20px', color: INK }}
      >
        {title}
      </Typography>
    </Box>
  );
}

function Divider() {
  return <Box data-testid="help-divider" sx={{ height: '1px', bgcolor: DIVIDER }} />;
}

function InfoCallout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box
      data-testid="help-info-callout"
      sx={{
        bgcolor: INFO_BOX.bg,
        border: `1px solid ${INFO_BOX.border}`,
        borderRadius: '12px',
        py: 1.5,
        px: 2,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <InfoOutlinedIcon sx={{ flexShrink: 0, fontSize: 20, color: INFO_BOX.accent, mt: '2px' }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: '20px', color: INK }}>
          {title}
        </Typography>
        <Typography sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: INK }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

function CautionCallout({ children }: { children: ReactNode }) {
  return (
    <Box
      data-testid="help-caution-callout"
      sx={{
        bgcolor: LTEL_BANNER.bg,
        border: `1px solid ${LTEL_BANNER.border}`,
        borderRadius: '12px',
        py: 1.5,
        px: 2,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <WarningAmberRoundedIcon
        sx={{ flexShrink: 0, fontSize: 20, color: LTEL_BANNER.accent, mt: '2px' }}
      />
      <Typography sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: LTEL_BANNER.fg }}>
        {children}
      </Typography>
    </Box>
  );
}

export function DataHelpDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 468 }, maxWidth: '100%' } }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 3,
          pb: 2.5,
          borderBottom: `1px solid ${DIVIDER}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 12,
              lineHeight: '16px',
              letterSpacing: '1px',
              color: GREY_300,
            }}
          >
            DATA HELP
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close data help"
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              bgcolor: GREY_25,
              color: TEXT_SECONDARY,
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
        <Typography sx={{ fontWeight: 800, fontSize: 24, lineHeight: '32px', color: INK }}>
          Preparing your data
        </Typography>
        <Typography
          sx={{ fontWeight: 400, fontSize: 16, lineHeight: '20px', color: TEXT_SECONDARY }}
        >
          Upload two files to get the most complete Program Evaluation and Student Action Plans.
        </Typography>
      </Box>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3, pb: 3.5 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FileRow n={1} title="WIDA ACCESS scores" subtitle="Student proficiency and growth" />
          <FileRow
            n={2}
            title="Student roster / SIS export"
            subtitle="Who students are and their program history"
          />
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <SectionHeading n={1} title="WIDA ACCESS scores" />
          <InfoCallout title="Upload the original, unaltered Summative State/District Student Response file from DRC Insight">
            Don&apos;t rename, reorder, or delete columns, or retype scores. Don&apos;t upload the
            ACCESS file your state sends you as this one does not have the exact data needed.
          </InfoCallout>
          <Typography
            sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: TEXT_SECONDARY }}
          >
            You&apos;ve got the right file if its headers include:
          </Typography>
          <ChipRow chips={WIDA_HEADERS} variant="blue" />
          <Typography sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: GREY_600 }}>
            Have several years? Upload each year&apos;s file; we group students across years for you.
          </Typography>
          <CautionCallout>
            Do not upload duplicate files. One ACCESS and one Alternate ACCESS file per year.
          </CautionCallout>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <SectionHeading n={2} title="Student roster / SIS export" />
          <Typography
            sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: TEXT_SECONDARY }}
          >
            Build this from your SIS; one row per student. We&apos;ll help you match your columns
            after upload.
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: 'normal',
              color: INK,
            }}
          >
            {MUST_INCLUDE_HEADING}
          </Typography>
          <ChipRow chips={SIS_MUST_INCLUDE} variant="blue" />
          <Typography sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: GREY_300 }}>
            Date fields use YYYY-MM-DD or M/D/YYYY — always a 4-digit year.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: 'normal',
                color: TEXT_SECONDARY,
              }}
            >
              {HELPFUL_HEADING}
            </Typography>
            <Typography sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: GREY_300 }}>
              Each field sharpens the insights and unlocks more student cohorts to compare.
            </Typography>
          </Box>
          <ChipRow chips={SIS_HELPFUL} variant="grey" />
          <CautionCallout>
            Without the must-include fields, parts of your reports can&apos;t be generated —
            reclassification &amp; exits, newcomer / long-term EL, and school-by-school.
          </CautionCallout>
        </Box>

        <Divider />

        <Box
          data-testid="help-most-important"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            bgcolor: GREY_25,
            borderRadius: '12px',
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 12,
              lineHeight: '16px',
              letterSpacing: '1px',
              color: GREY_300,
            }}
          >
            MOST IMPORTANT
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: '20px', color: INK }}>
            Use the same Student ID in both files.
          </Typography>
          <Typography sx={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: GREY_600 }}>
            We link a student by finding the identical ID across your files. Include both IDs where
            you have them: the State Student ID links to your WIDA file, and the District Student ID
            links to Flashlight360. Make sure each reads exactly the same everywhere — same digits,
            no dropped leading zeros. In Excel, format the ID columns as Text.
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
